import { mapInfo } from "@69ly/maps";
import { formatClock } from "@69ly/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCallouts } from "../lib/callouts.ts";
import { Navigate } from "react-router";
import { GrenadeIcon } from "../components/MarkerIcon.tsx";
import { StatusTag } from "../components/StatusControl.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { applyFilters, authorsOf, useFilters } from "../lib/filters.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { MapBar, useMapParam } from "./MapView.tsx";
import { MapFilters } from "../components/MapFilters.tsx";
import { StuffLink } from "../components/StuffDrawer.tsx";
import { ImportanceTag } from "../components/Importance.tsx";

/** The Utility page as a table: scan, search, compare windows and progress. */
export function StuffListPage() {
  const { t } = useTranslation();
  const map = useMapParam();
  const { scope } = useCurrentTeam();
  const stuffs = useStuffs(scope, map ?? "");
  const f = useFilters();
  const places = useCallouts();
  const shown = useMemo(
    () => applyFilters(stuffs.data ?? [], f.filters, (s) => (map ? Object.values(places.places(map, s)).filter(Boolean).join(" ") : "")),
    [stuffs.data, f.filters, places, map],
  );

  if (!map) return <Navigate to="/library" replace />;
  if (stuffs.isPending) return <Loading />;
  if (stuffs.error) return <ErrorState message={errorText(t, stuffs.error)} onRetry={() => void stuffs.refetch()} />;

  return (
    <div className="library">
      <MapBar map={map} view="list" count={stuffs.data.length} />
      <div className="page list-page">
        <div className="row">
          <MapFilters {...f} count={shown.length} authors={authorsOf(stuffs.data)} />
        </div>
        {shown.length === 0 ? (
          <div className="card">
            <p>{stuffs.data.length === 0 ? t("library.emptyTitle", { map: mapInfo(map).name }) : t("mapView.noMatch")}</p>
          </div>
        ) : (
          <div className="card table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("form.titleLabel")}</th>
                  <th scope="col">{t("form.side")}</th>
                  <th scope="col">{t("importance.label")}</th>
                  <th scope="col">{t("form.area")}</th>
                  <th scope="col">{t("mapView.windows")}</th>
                  <th scope="col">{t("mapView.myStatus")}</th>
                  <th scope="col">{t("team.eyebrow")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <StuffLink id={s.id} className="player-cell">
                        <GrenadeIcon grenade={s.grenade} side={s.side} size={24} />
                        {s.title}
                      </StuffLink>
                    </td>
                    <td>
                      <span className={`side-tag side-${s.side}`}>{t(`sideShort.${s.side}`)}</span>
                    </td>
                    <td>
                      <ImportanceTag importance={s.importance} />
                    </td>
                    <td>
                      {t(`areaShort.${s.area}`)}
                      {places.places(map, s).to && <span className="muted"> · {places.places(map, s).to}</span>}
                    </td>
                    <td className="mono small">
                      {s.windows.length ? s.windows.map((w) => `${formatClock(w.from)}–${formatClock(w.to)}`).join(", ") : "–"}
                    </td>
                    <td>
                      <StatusTag status={s.myStatus} />
                    </td>
                    <td className="muted">
                      {s.teamId ? `${s.progress.learned} / ${s.progress.members}` : "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
